/*
 * Poundsale homepage motion (GSAP 3 core + ScrollTrigger, loaded from cdnjs on the homepage only, see layout/theme.liquid).
 *
 * - Hero: on load and on every slide change (the slider fires "ps-hero:change") the headline lines slide in, the product
 *   pile rises in and the UK STOCK badge pops. While a slide is shown its front item floats gently and the flag layer
 *   moves a few pixels with the pointer (desktop pointer only).
 * - Sections: trust bar items, category tiles, brand plates, best-seller cards, why-shop items and the deals banner fade
 *   up once as they scroll into view.
 *
 * Safety rules:
 * - Does nothing when the visitor prefers reduced motion, inside the theme editor, or when GSAP did not load (the page
 *   is then exactly the static page: nothing is hidden by CSS while waiting for this script).
 * - Never touches the carousels' scroll-snap or click handlers; every inline style it adds is removed again once an
 *   animation has finished (clearProps), so hover / focus / snap behave exactly as without it.
 * - Everything is killed again on "shopify:section:unload".
 */
(function () {
  'use strict';

  var gsap = window.gsap;
  var ST = window.ScrollTrigger;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!gsap || reduce || (window.Shopify && window.Shopify.designMode)) return;
  if (ST) gsap.registerPlugin(ST);

  var cleanups = [];

  /* ------------------------------------------------------------------ hero */
  function initHero(hero) {
    var slides = Array.prototype.slice.call(hero.querySelectorAll('[data-slide]'));
    if (!slides.length) return;
    var flagInner = hero.querySelector('[data-flag-inner]');
    var current = slides.filter(function (s) { return s.classList.contains('is-active'); })[0] || slides[0];
    var floatTween = null;
    var hideTimers = [];

    function parts(slide) {
      return {
        lines: slide.querySelectorAll('.ps-hero__line1, .ps-hero__line2'),
        items: slide.querySelectorAll('.ps-hero__item'),
        badge: slide.querySelector('.ps-hero__badge'),
        front: slide.querySelector('.ps-hero__item--c img') || slide.querySelector('.ps-hero__item--c'),
      };
    }

    // Headline slide-in distance: 24px, but never more than the copy's left padding (on phones the neighbouring slide
    // is only a swipe away, so the text must not poke out of its own slide while it waits).
    function shift(slide) {
      var copy = slide.querySelector('.ps-hero__copy');
      var pad = copy ? parseFloat(window.getComputedStyle(copy).paddingLeft) : 24;
      return -Math.max(0, Math.min(24, isNaN(pad) ? 24 : pad));
    }

    function stopFloat() {
      if (floatTween) { floatTween.kill(); floatTween = null; }
    }

    // Slides that are off screen wait with their pile hidden (set by JS, so no-JS visitors see everything).
    function hide(slide) {
      var p = parts(slide);
      gsap.killTweensOf([p.lines, p.items, p.badge, p.front]);
      gsap.set(p.lines, { x: shift(slide) });
      gsap.set(p.items, { y: 40, opacity: 0 });
      if (p.badge) gsap.set(p.badge, { scale: 0.6, rotation: -12, opacity: 0 });
      if (p.front && p.front.tagName === 'IMG') gsap.set(p.front, { y: 0 });
    }

    function play(slide) {
      var p = parts(slide);
      stopFloat();
      gsap.killTweensOf([p.lines, p.items, p.badge]);
      var tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      if (p.lines.length) tl.fromTo(p.lines, { x: shift(slide) }, { x: 0, duration: 0.6, stagger: 0.1, clearProps: 'transform' }, 0);
      if (p.items.length) tl.fromTo(p.items, { y: 40, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6, stagger: 0.08, clearProps: 'transform,opacity' }, 0.05);
      if (p.badge) tl.fromTo(p.badge, { scale: 0.6, rotation: -12, opacity: 0 }, { scale: 1, rotation: 0, opacity: 1, duration: 0.6, ease: 'back.out(1.7)', clearProps: 'transform,opacity' }, 0.3);
      // (The endless idle float on the front item was removed at the owner's request: constant motion near the top of
      // the page read as the hero "sliding" while scrolling.)
    }

    slides.forEach(function (slide) { if (slide !== current) hide(slide); });
    play(current);

    function onChange(e) {
      var next = (e.detail && e.detail.slide) || null;
      if (!next || next === current) return;
      var prev = current;
      current = next;
      play(next);
      // let the old slide finish scrolling away before it is reset for its next entrance
      var t = setTimeout(function () { if (prev !== current) hide(prev); }, 900);
      hideTimers.push(t);
    }
    hero.addEventListener('ps-hero:change', onChange);

    // pause the float while the hero is off screen
    var io = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        hero._psOffscreen = !entries[0].isIntersecting;
        if (floatTween) floatTween.paused(hero._psOffscreen);
      });
      io.observe(hero);
    }

    // flag parallax, desktop pointer only, max 12px
    var onMove = null;
    var onLeave = null;
    if (flagInner && window.matchMedia('(pointer: fine)').matches) {
      var qx = gsap.quickTo(flagInner, 'x', { duration: 0.8, ease: 'power3' });
      var qy = gsap.quickTo(flagInner, 'y', { duration: 0.8, ease: 'power3' });
      onMove = function (e) {
        var r = hero.getBoundingClientRect();
        if (!r.width || !r.height) return;
        qx(((e.clientX - r.left) / r.width - 0.5) * -24);
        qy(((e.clientY - r.top) / r.height - 0.5) * -24);
      };
      onLeave = function () { qx(0); qy(0); };
      hero.addEventListener('pointermove', onMove, { passive: true });
      hero.addEventListener('pointerleave', onLeave);
    }

    cleanups.push(function () {
      hero.removeEventListener('ps-hero:change', onChange);
      if (onMove) hero.removeEventListener('pointermove', onMove);
      if (onLeave) hero.removeEventListener('pointerleave', onLeave);
      if (io) io.disconnect();
      hideTimers.forEach(clearTimeout);
      stopFloat();
      var all = hero.querySelectorAll('.ps-hero__line1, .ps-hero__line2, .ps-hero__item, .ps-hero__item img, .ps-hero__badge, [data-flag-inner]');
      gsap.killTweensOf(all);
      gsap.set(all, { clearProps: 'all' });
    });
  }

  /* -------------------------------------------------------- scroll reveals */
  // Scroll-in reveals are switched off (owner: cards rising/fading in while scrolling past the battery rows felt like the
  // page was sliding). Products now render in place immediately; add selectors here to bring a reveal back.
  var REVEAL = [];

  function initReveals() {
    if (!ST) return;
    var all = [];
    REVEAL.forEach(function (sel) {
      var els = gsap.utils.toArray(sel);
      if (!els.length) return;
      all = all.concat(els);
      gsap.set(els, { opacity: 0, y: 24 });
      ST.batch(els, {
        start: 'top 85%',
        once: true,
        interval: 0.1,
        batchMax: 12,
        onEnter: function (batch) {
          gsap.to(batch, { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out', stagger: 0.06, overwrite: true, clearProps: 'opacity,transform' });
        },
      });
    });

    // Safety net: anything still hidden although it is on screen (late layout shifts, odd scroll positions) is shown.
    var rescue = function () {
      all.forEach(function (el) {
        if (!el.isConnected || gsap.getProperty(el, 'opacity') > 0.01) return;
        if (gsap.isTweening(el)) return;
        var r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) {
          gsap.to(el, { opacity: 1, y: 0, duration: 0.4, overwrite: true, clearProps: 'opacity,transform' });
        }
      });
    };
    var timer = null;
    var onScroll = function () { clearTimeout(timer); timer = setTimeout(rescue, 900); };
    window.addEventListener('scroll', onScroll, { passive: true });
    var loadTimer = setTimeout(rescue, 4500);
    cleanups.push(function () {
      window.removeEventListener('scroll', onScroll);
      clearTimeout(timer);
      clearTimeout(loadTimer);
      ST.getAll().forEach(function (t) { t.kill(); });
      gsap.killTweensOf(all);
      gsap.set(all, { clearProps: 'opacity,transform' });
    });
  }

  /* ------------------------------------------------------------------ boot */
  function destroy() {
    cleanups.splice(0).forEach(function (fn) { try { fn(); } catch (err) { /* ignore */ } });
  }

  function boot() {
    var hero = document.querySelector('ps-hero-slider');
    if (hero) initHero(hero);
    initReveals();
  }

  document.addEventListener('shopify:section:unload', destroy);
  window.addEventListener('pagehide', destroy);

  try {
    boot();
  } catch (err) {
    // Never leave content hidden because of a script problem
    destroy();
  }
})();
